<template>
  <GuidesLayout>
    <div class="wrap">
      <section class="guides-hero">
        <div class="guides-eyebrow">{{ tag ? 'Forever-X Guides · Tag' : 'Forever-X Guides' }}</div>
        <h1 class="guides-title">{{ heading }}</h1>
        <p class="guides-sub">{{ intro }}</p>
      </section>

      <nav class="cat-bar" aria-label="Guide categories">
        <router-link to="/guides" class="cat-pill" :class="{ active: !categorySlug && !tag }">All <span class="count">({{ totalCount }})</span></router-link>
        <router-link
          v-for="c in categories"
          :key="c.slug"
          :to="categoryPath(c.slug)"
          class="cat-pill"
          :class="{ active: categorySlug === c.slug }"
        >
          {{ c.name }} <span class="count">({{ c.count || 0 }})</span>
        </router-link>
      </nav>

      <div v-if="loading" class="guides-loading">Loading guides…</div>
      <div v-else-if="error" class="guides-empty"><b>Something went wrong</b>{{ error }}</div>
      <template v-else>
        <section v-if="featuredPost" class="featured" aria-label="Featured guide">
          <router-link class="featured-media" :to="guidePath(featuredPost.slug)" tabindex="-1" aria-hidden="true">
            <img v-if="featuredPost.heroImageUrl" :src="featuredPost.heroImageUrl" :alt="featuredPost.heroImageAlt || ''">
          </router-link>
          <div>
            <div class="featured-eyebrow">Featured guide</div>
            <h2 class="featured-title"><router-link :to="guidePath(featuredPost.slug)">{{ featuredPost.title }}</router-link></h2>
            <p class="featured-excerpt">{{ featuredPost.metaDescription }}</p>
            <div class="featured-meta">
              <router-link class="cat-tag" :to="categoryPath(featuredPost.categorySlug)">{{ featuredPost.category }}</router-link>
              <span>{{ formatGuideDate(featuredPost.publishedAt) }}</span><span>·</span><span>{{ featuredPost.readMinutes }} min read</span>
            </div>
            <div class="featured-actions">
              <router-link class="btn btn-primary" :to="guidePath(featuredPost.slug)">Read guide</router-link>
              <button type="button" class="card-share" @click="copyLink(guideUrl(featuredPost.slug))">Copy link</button>
            </div>
          </div>
        </section>

        <div v-if="gridPosts.length" class="guides-grid">
          <GuideCard v-for="post in gridPosts" :key="post.id" :post="post" />
        </div>
        <div v-else-if="!featuredPost" class="guides-empty">
          <b>More guides coming soon</b>
          <template v-if="categorySlug">Nothing in {{ category?.name || 'this category' }} yet. <router-link to="/guides">See all guides</router-link>.</template>
          <template v-else-if="tag">No guides carry this tag right now. <router-link to="/guides">See all guides</router-link>.</template>
          <template v-else>We’re writing the first ones now.</template>
        </div>
      </template>

      <section class="cta-banner">
        <div class="cta-banner-text">
          <h2 class="cta-banner-head">Planning something of your own?</h2>
          <p>Start a free memorial page, share one link, and let everyone add their own photos and stories — no login required for them to contribute.</p>
        </div>
        <a class="btn btn-primary" href="/who" @click.prevent="createMemorial">Create a Memorial</a>
      </section>
    </div>
  </GuidesLayout>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import apiService from '../services/api'
import GuidesLayout from '../components/guides/GuidesLayout.vue'
import GuideCard from '../components/guides/GuideCard.vue'
import { categoryPath, formatGuideDate, guidePath, guideUrl, tagPath } from '../components/guides/guideFormat'
import { useGuideToast } from '../components/guides/useGuideToast'
import { useStartMemorial } from '../composables/useStartMemorial'
import { clearPageMeta, setPageMeta } from '../utils/seo'

const INDEX_INTRO = 'No stock eulogy lines. No generic checklists lifted from somewhere else. Just practical, specific guides for planning a send-off — or writing a tribute — that actually fits the person you’re doing it for.'

// Hub-page intros so each indexable category page has its own copy
const CATEGORY_INTROS = {
  'planning-guides': 'Practical, step-by-step help for planning a send-off that sounds like them — the place, the people, the music, and what to keep afterward.',
  'eulogy-tribute': 'Help with the hardest part to write: finding the stories, the words, and the details people will actually remember.',
  'memorial-inspiration': 'Ideas for remembering someone the way they actually lived — before, during, and long after the day itself.'
}

const route = useRoute()
const { copyLink } = useGuideToast()
const { createMemorial } = useStartMemorial()

const posts = ref([])
const categories = ref([])
const category = ref(null)
const tagInfo = ref(null)
const loading = ref(true)
const error = ref('')

const categorySlug = computed(() => route.params.category || null)
const tag = computed(() => route.params.tag || null)
const totalCount = computed(() => categories.value.reduce((sum, c) => sum + (c.count || 0), 0))

const heading = computed(() => {
  if (categorySlug.value) return category.value?.name || 'Guides'
  if (tag.value) return tagInfo.value?.name || tag.value
  return 'Planning help that sounds like them, not a funeral home brochure.'
})
const intro = computed(() => {
  if (categorySlug.value) return CATEGORY_INTROS[categorySlug.value] || INDEX_INTRO
  if (tag.value) return `Every Forever-X guide tagged “${tagInfo.value?.name || tag.value}”.`
  return INDEX_INTRO
})

// The featured block only shows on the main index: the post flagged featured, else the newest
const featuredPost = computed(() => {
  if (categorySlug.value || tag.value || !posts.value.length) return null
  return posts.value.find(p => p.featured) || posts.value[0]
})
const gridPosts = computed(() => posts.value.filter(p => p !== featuredPost.value))

const updateMeta = () => {
  if (categorySlug.value) {
    const name = category.value?.name || 'Guides'
    setPageMeta({ title: `${name} — Forever-X Guides`, description: intro.value, path: categoryPath(categorySlug.value), robots: category.value ? 'index, follow' : 'noindex, follow' })
  } else if (tag.value) {
    const name = tagInfo.value?.name || tag.value
    // Tag archives are thin at this scale — noindex unless a post has opted its tags in
    setPageMeta({ title: `${name} — Forever-X Guides`, description: intro.value, path: tagPath(tag.value), robots: tagInfo.value?.indexable ? 'index, follow' : 'noindex, follow' })
  } else {
    setPageMeta({ title: 'Guides — Forever-X', description: INDEX_INTRO, path: '/guides', image: featuredPost.value?.heroImageUrl })
  }
}

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const result = await apiService.listGuides({ category: categorySlug.value, tag: tag.value })
    if (!result.success) throw new Error(result.msg || 'Unable to load guides')
    posts.value = result.blogs || []
    categories.value = result.categories || []
    category.value = result.category || null
    tagInfo.value = result.tag || null
  } catch (e) {
    posts.value = []
    error.value = e.message || 'Unable to load guides'
  } finally {
    loading.value = false
    updateMeta()
  }
}

watch(() => route.fullPath, () => { if (route.path.startsWith('/guides')) load() }, { immediate: true })
onBeforeUnmount(() => clearPageMeta())
</script>
